import React from 'react';

import Icon from 'views/site/Icon.js';

import Section, { SectionHeader } from 'views/site/Section.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * WhyDifferent
 *
 * The storefront's split card — a tinted rail carrying the one number that
 * matters, beside the list of what that number buys — followed by the
 * minimum-versus-optimum comparison the in-home presentation builds toward.
 *
 * Both panels are contained cards on the off-white ground. The storefront
 * never runs a table full width and never introduces a new ground to make a
 * point, so neither does this.
 *
 * A note on the comparison: the storefront deliberately makes no claims about
 * other brands, because it is selling to cold traffic that has no frame of
 * reference. A replicated website is different — the advisor's whole middle
 * act is "these are the alarms you already own, here is what they cannot do" —
 * so the comparison stays, but every row is a factual capability difference
 * rather than a swipe, and the left column is what the building code actually
 * requires. That is the point being made, not an insult.
 */

const WhyDifferent = () => {

    const mobile = Utils.isMobile();

    const capabilities = [
        'Smoke, heat, carbon monoxide, water and freeze on one network',
        'Any device that detects sounds every other device',
        'Tells you which room the danger is in',
        '520 Hz horn plus an under-mattress shaker',
        'Text, email and app alerts to 8 contacts',
        'Sealed 10-year batteries, nothing to replace',
        'Its own private radio, no Wi-Fi to drop',
        'Professionally installed and explained to your family'
    ];

    const rows = [
        { minimum: 'Sounds only in the room it is in', network: 'One alarm sounds every device in the house' },
        { minimum: 'No way to tell you where', network: 'Tells you which room the danger is in' },
        { minimum: 'A horn, two rooms away', network: '520 Hz horn plus a shaker under the mattress' },
        { minimum: 'Nothing reaches you once you leave', network: 'Text, email and push notifications' },
        { minimum: 'Nuisance-alarms in kitchens and garages', network: 'Heat sensors rated for those rooms' },
        { minimum: 'Carbon monoxide is a separate device', network: 'Carbon monoxide on the same network' },
        { minimum: 'Water damage is not covered at all', network: 'Leak and freeze on the same network' },
        { minimum: 'Batteries to replace or pull out', network: 'Sealed 10-year batteries' },
        { minimum: 'Dust ends its life quietly', network: 'Chamber lifts out and can be cleaned' },
        { minimum: 'Buy another after a fire', network: 'Lifetime fire/flood replacement guarantee' }
    ];

    return (
        <Section id={'why-different'} tinted={true}>
            {/*
              * This section has one job: the cost objection. It says what the
              * $0 is (the monitoring fee) and what it is not (the payment on a
              * financed network), and stops there. Everything else people ask
              * about money is in the FAQ, which is where an objection can be
              * answered at length without diluting the claim on the page.
              *
              * Two things the copy deliberately does not say. It does not
              * claim there is nothing between the house and the phone, because
              * the alerts travel over our own infrastructure and a claim that
              * cannot survive a follow-up question is not worth making. And it
              * does not mention what the network costs to run, which is a
              * detail nobody is weighing at this point in the page.
              */}
            <SectionHeader
            eyebrow={'Why the network'}
            subtitle={'The network is self-monitored. When a sensor detects something, the alert goes to your phone and to the contacts you choose rather than to a call center that bills you every month to watch it. The network itself can be paid up front or over time, and it is yours either way.'}
            title={'Full protection with no monitoring fees'} />

            {/* Split card — tinted rail beside the capability list */}
            <div style={{
                backgroundColor: Theme.colors.white,
                border: `1px solid ${Theme.colors.border()}`,
                borderRadius: 24,
                boxShadow: Theme.boxShadow.card,
                display: 'flex',
                flexDirection: mobile ? 'column' : 'row',
                margin: '0 auto',
                maxWidth: 1000,
                overflow: 'hidden'
            }}>
                <div style={{
                    alignItems: 'center',
                    backgroundColor: Theme.tint(0.05),
                    borderBottom: mobile ? `1px solid ${Theme.colors.border()}` : 'none',
                    borderRight: mobile ? 'none' : `1px solid ${Theme.colors.border()}`,
                    display: 'flex',
                    flex: mobile ? 'none' : '0 0 42%',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: mobile ? '44px 30px' : '60px 52px',
                    textAlign: 'center'
                }}>
                    <span style={{
                        ...Theme.textStyles.label(),
                        display: 'block',
                        marginBottom: 16
                    }}>{'No monthly monitoring fee'}</span>
                    <div style={{
                        alignItems: 'baseline',
                        display: 'inline-flex',
                        gap: 6,
                        justifyContent: 'center'
                    }}>
                        <span style={{
                            color: Theme.colors.text(),
                            fontFamily: Theme.fonts.heading,
                            fontSize: mobile ? 88 : 108,
                            fontWeight: 600,
                            letterSpacing: '-0.05em',
                            lineHeight: 1
                        }}>{'$0'}</span>
                        <span style={{
                            color: Theme.colors.subText(),
                            fontFamily: Theme.fonts.heading,
                            fontSize: mobile ? 20 : 24,
                            fontWeight: 600,
                            letterSpacing: '-0.02em'
                        }}>{'/mo'}</span>
                    </div>
                    {/* Two chips rather than one. The first is the promise, the
                        second is what a financed customer needs to see beside
                        it so the $0 reads as the monitoring fee it is. */}
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                        justifyContent: 'center',
                        margin: '24px auto 0'
                    }}>
                        {[
                            { icon: 'shield-check', label: 'You own it outright' },
                            { icon: 'cash-coin', label: 'Payment plans available' }
                        ].map(chip => (
                            <div
                            key={chip.label}
                            style={{
                                alignItems: 'center',
                                backgroundColor: Theme.colors.white,
                                border: `1px solid ${Theme.colors.border()}`,
                                borderRadius: 999,
                                display: 'inline-flex',
                                gap: 8,
                                padding: '9px 16px'
                            }}>
                                <Icon name={chip.icon} size={12} color={Theme.colors.primary()} />
                                <span style={{
                                    ...Theme.textStyles.caption({ color: Theme.colors.text(), fontWeight: 600 }),
                                    whiteSpace: 'nowrap'
                                }}>{chip.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: mobile ? '12px 26px 28px' : '28px 44px'
                }}>
                    {capabilities.map((capability, index) => (
                        <div
                        key={index}
                        style={{
                            alignItems: 'center',
                            borderBottom: index === capabilities.length - 1 ? 'none' : `1px solid ${Theme.colors.border()}`,
                            display: 'flex',
                            gap: 14,
                            padding: '13px 0'
                        }}>
                            <div style={{
                                alignItems: 'center',
                                backgroundColor: Theme.tint(0.1),
                                borderRadius: '50%',
                                display: 'flex',
                                flexShrink: 0,
                                height: 26,
                                justifyContent: 'center',
                                width: 26
                            }}>
                                <Icon name={'check-lg'} size={11} color={Theme.colors.primary()} />
                            </div>
                            <span style={Theme.textStyles.body({ color: Theme.colors.text(), fontSize: mobile ? 15 : 16, fontWeight: 500 })}>
                                {capability}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* The comparison, as a second contained panel */}
            <div style={{ marginTop: mobile ? 56 : 88 }}>
                <div style={{
                    margin: '0 auto',
                    marginBottom: mobile ? 32 : 44,
                    maxWidth: 640,
                    textAlign: 'center'
                }}>
                    <span style={{
                        ...Theme.textStyles.heading({ fontSize: mobile ? 24 : 32, textAlign: 'center' }),
                        display: 'block',
                        marginBottom: 14
                    }}>{'Better than the bare minimum required by law'}</span>
                    <span style={{
                        ...Theme.textStyles.sectionSubheading({ margin: '0 auto' }),
                        display: 'block'
                    }}>{'The alarms on the shelf at the hardware store meet the code. Meeting the code and protecting a sleeping family turn out to be two different problems.'}</span>
                </div>

                <div style={{
                    backgroundColor: Theme.colors.white,
                    border: `1px solid ${Theme.colors.border()}`,
                    borderRadius: 24,
                    boxShadow: Theme.boxShadow.card,
                    margin: '0 auto',
                    maxWidth: 1000,
                    overflow: 'hidden'
                }}>
                    <div style={{
                        borderBottom: `1px solid ${Theme.colors.border()}`,
                        display: 'grid',
                        gridTemplateColumns: mobile ? '1fr' : '1fr 1fr'
                    }}>
                        <div style={{
                            borderRight: mobile ? 'none' : `1px solid ${Theme.colors.border()}`,
                            padding: mobile ? '16px 20px 12px' : '20px 24px'
                        }}>
                            <span style={Theme.textStyles.subHeading({ color: Theme.colors.subText(), fontSize: 16 })}>
                                {'A store-bought alarm'}
                            </span>
                        </div>
                        {mobile === false && (
                            <div style={{
                                backgroundColor: Theme.tint(0.05),
                                padding: '20px 24px'
                            }}>
                                <span style={Theme.textStyles.subHeading({ fontSize: 16 })}>
                                    {'The connected network'}
                                </span>
                            </div>
                        )}
                    </div>

                    {rows.map((row, index) => (
                        <div
                        key={index}
                        style={{
                            borderTop: index === 0 ? 'none' : `1px solid ${Theme.colors.divider()}`,
                            display: 'grid',
                            gridTemplateColumns: mobile ? '1fr' : '1fr 1fr'
                        }}>
                            <div style={{
                                alignItems: 'center',
                                borderRight: mobile ? 'none' : `1px solid ${Theme.colors.border()}`,
                                display: 'flex',
                                gap: 10,
                                padding: mobile ? '12px 20px' : '15px 24px'
                            }}>
                                <Icon name={'dash-lg'} size={20} color={Theme.colors.lightGrey} style={{ flexShrink: 0, paddingTop: 5 }} />
                                <span style={Theme.textStyles.body({ fontSize: 15 })}>{row.minimum}</span>
                            </div>
                            <div style={{
                                alignItems: 'center',
                                backgroundColor: Theme.tint(0.05),
                                borderTop: mobile ? `1px solid ${Theme.colors.divider()}` : 'none',
                                display: 'flex',
                                gap: 10,
                                padding: mobile ? '12px 20px' : '15px 24px'
                            }}>
                                <Icon name={'check-lg'} size={20} color={Theme.colors.success} style={{ flexShrink: 0, paddingTop: 5 }} />
                                <span style={Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 15, fontWeight: 500 })}>
                                    {row.network}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </Section>
    )
}

export default WhyDifferent;
